import { useTranslation } from 'react-i18next'
import { formatMoneyCents } from '@/shared/lib/formatMoney'
import { cn } from '@/shared/lib/utils'
import type { PriceBreakdown as PriceBreakdownData } from '@/shared/schemas/priceBreakdown.schema'

type PriceBreakdownProps = {
  breakdown: PriceBreakdownData
  showSubsidy?: boolean
  className?: string
}

export function PriceBreakdown({ breakdown, showSubsidy = true, className }: PriceBreakdownProps) {
  const { t, i18n } = useTranslation()
  const locale = (i18n.resolvedLanguage ?? i18n.language).replaceAll('_', '-')
  const showBase = breakdown.show_base_and_subsidized_price && showSubsidy
  const beforeSubsidy = breakdown.discount_application === 'before_subsidy'
  const rows: { key: string; label: string; amount: number }[] = []

  if ((showBase || breakdown.subsidy_amount_cents === 0) && breakdown.lines.length > 0) {
    for (const line of breakdown.lines) {
      rows.push({
        key: `line-${line.room_type_id}`,
        label: t('price_breakdown.line_base', { label: line.label, count: line.rooms_count }),
        amount: line.base_amount_cents,
      })
    }
  } else if (showBase || breakdown.subsidy_amount_cents === 0) {
    const firstNight = breakdown.nightly_amounts_cents[0]
    const hasUniformNightlyRate =
      breakdown.granularity === 'per_night' &&
      firstNight != null &&
      breakdown.nightly_amounts_cents.every((amount) => amount === firstNight)
    rows.push({
      key: 'base',
      label: breakdown.granularity === 'per_stay'
        ? t('price_breakdown.stay_base')
        : hasUniformNightlyRate
          ? t('price_breakdown.nightly_calculation', {
              count: breakdown.night_count,
              amount: formatMoneyCents(firstNight, breakdown.currency, locale),
            })
          : t('price_breakdown.nightly_base', { count: breakdown.night_count }),
      amount: breakdown.base_amount_cents,
    })
  }

  let discountLabel = t(`price_breakdown.discount.${breakdown.discount_type ?? 'generic'}`)
  if (breakdown.discount_type === 'percent' && breakdown.discount_percent_basis_points != null) {
    discountLabel = t('price_breakdown.discount_percent', {
      percent: new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 })
        .format(breakdown.discount_percent_basis_points / 10_000),
    })
  } else if (
    breakdown.discount_type === 'free_nights' &&
    breakdown.free_nights_count != null &&
    breakdown.free_nights_mode !== 'extension'
  ) {
    discountLabel = t('price_breakdown.free_nights', { count: breakdown.free_nights_count })
  }

  const discount = {
    key: 'discount',
    label: discountLabel,
    amount: -breakdown.discount_amount_cents,
  }
  if (beforeSubsidy && breakdown.discount_amount_cents > 0) {
    rows.push(discount)
  }

  if (showBase && breakdown.subsidy_amount_cents > 0) {
    rows.push({ key: 'subsidy', label: t('price_breakdown.subsidy'), amount: -breakdown.subsidy_amount_cents })
    rows.push({
      key: 'subsidized',
      label: t('price_breakdown.subsidized'),
      amount: (beforeSubsidy ? breakdown.subsidy_base_amount_cents : breakdown.base_amount_cents) -
        breakdown.subsidy_amount_cents,
    })
  }

  if (!beforeSubsidy && breakdown.discount_amount_cents > 0) {
    rows.push(discount)
  }
  if (breakdown.last_minute_discount_amount_cents > 0) {
    rows.push({
      key: 'last-minute',
      label: t('price_breakdown.last_minute'),
      amount: -breakdown.last_minute_discount_amount_cents,
    })
  }
  if (breakdown.free_nights_mode === 'extension' && breakdown.free_nights_count != null) {
    rows.push({
      key: 'free-extension',
      label: t('price_breakdown.free_extension', { count: breakdown.free_nights_count }),
      amount: 0,
    })
  }
  rows.push({ key: 'total', label: t('price_breakdown.total_stay'), amount: breakdown.total_amount_cents })

  const bestOfMessage =
    showSubsidy && breakdown.discount_application === 'best_of'
      ? breakdown.discount_amount_cents > 0
        ? t('price_breakdown.best_of_discount')
        : breakdown.subsidy_amount_cents > 0
          ? t('price_breakdown.best_of_subsidy')
          : null
      : null

  return (
    <div className={cn('space-y-1 text-sm text-foreground', className)}>
      <dl aria-label={t('price_breakdown.title')} className="space-y-1">
        {rows.map((row) => (
          <div
            key={row.key}
            className={cn('flex flex-wrap justify-between gap-x-4 gap-y-1', row.key === 'total' && 'font-semibold')}
          >
            <dt>{row.label}</dt>
            <dd className="tabular-nums">{formatMoneyCents(row.amount, breakdown.currency, locale)}</dd>
          </div>
        ))}
      </dl>
      {bestOfMessage ? <p className="text-xs text-muted-foreground">{bestOfMessage}</p> : null}
    </div>
  )
}
